import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React from 'react';

import isArchived from 'archive/isArchived';
import {ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {SingleValueFilter} from 'search/SearchConstants';
import pickDefined from 'util/pickDefined';

import {ProcessTaskSchemaFilterFormQuery} from './__generated/ProcessTaskSchemaFilterFormQuery';
import DropdownFilterForm, {prefixedToggleText} from './base/DropdownFilterForm';
import {BASE_FILTER_FORM_PROP_NAMES, BaseFilterFormProps, FilterStyle} from './base/FilterFormConstants';
import useFilterArchivedDropdownOptions, {
  ArchivableOptionData,
} from './base/useFilterArchivedDropdownOptions';

type ProcessTaskSchema =
  ProcessTaskSchemaFilterFormQuery['processTaskSchemasSupportingPlatformFeature'][number];

export const PROCESS_TASK_SCHEMA_FILTER_FORM_QUERY: DocumentNode = gql`
  query ProcessTaskSchemaFilterFormQuery @skipAudit @engComponent(name: SIGHTGLASS) {
    processTaskSchemasSupportingPlatformFeature(platformFeature: WORKFLOWS_SEARCH) {
      id
      name
      executionType
      archiveRecord {
        purpose
      }
    }
  }
`;

interface ProcessTaskSchemaFilterFormProps extends BaseFilterFormProps {
  filter: SingleValueFilter;
  filterStyle: FilterStyle;
  onChangeFilter: (filter: SingleValueFilter) => void;
  onRemove?: () => void;
}

export interface ProcessTaskSchemaFilterFormExtraProps {
  schemaExecutionTypes: Array<ProcessExecutionType>;
}

function _buildOptionDatas(processTaskSchemas: Array<ProcessTaskSchema>): Array<ArchivableOptionData> {
  return processTaskSchemas.map((processTaskSchema) => ({
    name: processTaskSchema.name,
    value: processTaskSchema.id,
    isArchived: isArchived(processTaskSchema),
  }));
}

function filterSchemas(
  schemas: Array<ProcessTaskSchema>,
  executionTypes: Array<ProcessExecutionType>
): Array<ProcessTaskSchema> {
  return schemas.filter((schema) => executionTypes.includes(schema.executionType));
}

function ProcessTaskSchemaFilterForm(
  props: ProcessTaskSchemaFilterFormProps & ProcessTaskSchemaFilterFormExtraProps
): JSX.Element {
  const {filter, onChangeFilter, filterStyle, onRemove, schemaExecutionTypes} = props;
  const skeletonProps = pickDefined(props, ...BASE_FILTER_FORM_PROP_NAMES);
  const onChangeValueBase = (value: string): void => {
    if (value === filter.value) {
      onChangeFilter({...filter, value: null});
    } else {
      onChangeFilter({...filter, value});
    }
  };
  const filterArchivedDropdownOptionProps = useFilterArchivedDropdownOptions(
    [],
    'Show archived schemas',
    onChangeValueBase
  );
  return (
    <BenchlingQuery<ProcessTaskSchemaFilterFormQuery>
      query={PROCESS_TASK_SCHEMA_FILTER_FORM_QUERY}
      renderLoading={() => (
        <DropdownFilterForm
          {...skeletonProps}
          {...filterArchivedDropdownOptionProps}
          dropdownToggleText={prefixedToggleText('Type', [], filter)}
          filterStyle={filterStyle}
          isMulti={false}
          isSearchable={true}
          onRemove={onRemove}
          value={filter.value}
        />
      )}
    >
      {({processTaskSchemasSupportingPlatformFeature: processTaskSchemas}) => {
        const optionDatas = _buildOptionDatas(filterSchemas(processTaskSchemas, schemaExecutionTypes));
        return (
          <DropdownFilterForm
            {...skeletonProps}
            {...filterArchivedDropdownOptionProps}
            optionDatas={optionDatas}
            dropdownToggleText={prefixedToggleText('Type', optionDatas, filter)}
            filterStyle={filterStyle}
            isMulti={false}
            isSearchable={true}
            onRemove={onRemove}
            value={filter.value}
          />
        );
      }}
    </BenchlingQuery>
  );
}

export default ProcessTaskSchemaFilterForm;
